import { observer } from "mobx-react-lite";
import { ReactElement, useMemo, useState } from "react";
import { AddIcon, EditIcon, MinusIcon } from "@chakra-ui/icons";
import {
  Box,
  Center,
  Flex,
  FlexProps,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverBodyProps,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spacer,
  Text,
} from "@chakra-ui/react";

import {
  ColorType,
  ColorAndIndex,
  ISVGNode,
  Dictionary,
  ImageFilterParams,
  imageFilterSettings,
  shallowCopy,
  sortBy,
  range,
  IPathOptions,
  APIOption,
  APIType,
  Lang,
} from "@noli/core";
import { apiSchema } from "@noli/svg";
import {
  translate,
  useFilters,
  useNodeCheck,
  useSelecting,
  useSvgColorChannels,
  useChangeSvgColorChannels,
  INoliTextFrameUIInfoResponse,
  ICommonNoliFrameUIInfoResponse,
  NoliFrameRepeatLayer,
  useDefaults,
  langStore,
} from "@noli/business";

import { themeStore } from "@/stores/theme";
import { getSubEditorTitleProps } from "@/styles/subEditor";
import SubPanel, { ISubPanel, SubPanelTitleWithIcon } from "@/components/SubPanel";
import IconButton from "@/components/IconButton";
import Select from "@/components/Select";
import ColorPicker from "@/components/ColorPicker";
import SubPanelWithScrollBar from "@/components/SubPanel/WithScrollBar";
import SubEditorPanel from "@/components/SubPanel/SubEditor";
import SchemaProperty, { SchemaValueType } from "@/components/Property/SchemaProperty";

export interface IPopoverPanel extends PopoverBodyProps {
  contentW?: string;
  header: string;
  iconButton: ReactElement;
}
export function PopoverPanel({ contentW, header, iconButton, children, ...others }: IPopoverPanel) {
  return (
    <Popover>
      <PopoverTrigger>{iconButton}</PopoverTrigger>
      <Portal>
        <PopoverContent w={contentW}>
          <PopoverArrow />
          <PopoverHeader color="#333" fontSize="12px" fontWeight={700}>
            {header}
          </PopoverHeader>
          <PopoverCloseButton />
          <PopoverBody {...others}>{children}</PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

const noSvgColorsDisplay = (
  <SubEditorPanel h="108px" panelTitle="颜色通道">
    <Text px="16px" py="4px" color="#939393" fontSize="12px" userSelect="none">
      未检测到颜色通道
    </Text>
  </SubEditorPanel>
);
export const SvgColorsPanel = observer(() => {
  const node = useSelecting("raw").displayNode;
  const colorChannels = useSvgColorChannels();
  const { borderColor } = themeStore.styles;

  if (!colorChannels || node?.type !== "svg") {
    return noSvgColorsDisplay;
  }

  function getHandler(
    i: number,
    color: ColorType,
    node: ISVGNode,
    colorChannels: ColorAndIndex,
    trace: boolean,
  ): (color: ColorType) => void {
    return useChangeSvgColorChannels(i, node, colorChannels, color, trace);
  }

  const targetIndices: number[] = [];
  colorChannels.colorArray.forEach((color, i) => {
    if (color.type !== "RADIAL") {
      targetIndices.push(i);
    }
  });
  if (targetIndices.length === 0) {
    return noSvgColorsDisplay;
  }
  const colorArray = targetIndices.map((i) => colorChannels.colorArray[i]);
  const colorNums = targetIndices.map((i) => colorChannels.colorIndexArr[i].length);
  const sortedColors = sortBy(colorArray, colorNums.reverse());
  return (
    <SubEditorPanel h="108px" panelTitle="颜色通道">
      <Flex pl="16px" py="8px" wrap="wrap">
        {sortedColors.map((color, i) => {
          return (
            <ColorPicker
              key={i}
              originalColor={color}
              onColorChange={getHandler(i, color, node, colorChannels, false)}
              onColorChangeComplete={getHandler(i, color, node, colorChannels, true)}
              mb="12px"
              mr="12px"
              cursor="pointer"
              border="1px"
              borderColor={borderColor}
            />
          );
        })}
      </Flex>
    </SubEditorPanel>
  );
});

const EXCLUDED_FILTERS = new Set(["pixelate"]);
const EXCLIDED_PARAMS: Dictionary<Set<string>> = {
  dropShadow: new Set(["shadowOnly"]),
};
interface IFilterData {
  alias: string;
  index: number;
  filters: ImageFilterParams[];
  setFilters: ({ trace }: { trace: boolean }) => (filters: ImageFilterParams[]) => void;
  excluded?: string[];
}
function FilterEditor({ alias, index, filters, setFilters, lang }: IFilterData & { lang: Lang }) {
  const type = filters[index].type;
  const schema = imageFilterSettings[type];
  const excludedKeys = EXCLIDED_PARAMS[type] ?? new Set();
  const targetSchema = Object.entries(schema).filter(([key]) => !excludedKeys.has(key));
  if (targetSchema.length === 0) {
    return <Box w="32px" h="32px"></Box>;
  }

  function setValue(opt: { trace: boolean }): (value: SchemaValueType, apiKey: string) => void {
    return (value, apiKey) => {
      const localFilters = shallowCopy(filters ?? []);
      localFilters[index].params[apiKey] = value;
      setFilters(opt)(localFilters);
    };
  }

  return (
    <PopoverPanel
      contentW="240px"
      header={translate(filters[index].type, lang)}
      iconButton={<IconButton icon={EditIcon} />}
    >
      <SubPanelWithScrollBar w="100%" h="200px">
        {targetSchema.map(([key, apiOption]) => (
          <Center key={`${index}${key}`} w="100%" pr="16px">
            <SchemaProperty
              identifier={`${alias}-${index}-${key}`}
              lang={lang}
              apiKey={key}
              apiOption={apiOption}
              value={filters[index].params[key]}
              setValue={setValue}
            />
          </Center>
        ))}
      </SubPanelWithScrollBar>
    </PopoverPanel>
  );
}
const FilterTab = observer<IFilterData>(({ alias, index, filters, setFilters, excluded }) => {
  const lang = langStore.tgt;

  excluded ??= [];
  const availableFilters = Object.keys(imageFilterSettings).filter(
    (f) => !EXCLUDED_FILTERS.has(f) && !excluded?.includes(f),
  );

  return (
    <Flex w="100%" h="32px" pb="4px">
      <Flex flex={1}>
        <FilterEditor
          alias={alias}
          index={index}
          filters={filters}
          setFilters={setFilters}
          lang={lang}
        />
        <Select
          flex={1}
          h="32px"
          pl="12px"
          iconProps={{ pr: "8px" }}
          value={translate(filters[index].type, lang)}
          options={availableFilters}
          optionConverter={(option: string) => translate(option, lang)}
          onOptionClick={(filter: string) => {
            const localFilters = shallowCopy(filters ?? []);
            localFilters[index].type = filter;
            localFilters[index].params = {};
            setFilters({ trace: true })(localFilters);
          }}
        />
      </Flex>
      <IconButton
        icon={MinusIcon}
        onClick={() => {
          const localFilters = shallowCopy(filters ?? []);
          localFilters.splice(index, 1);
          setFilters({ trace: true })(localFilters);
        }}
      />
    </Flex>
  );
});
interface IFiltersPanel extends ISubPanel {
  alias: string;
  defaultFilter?: string;
  excludedFilters?: string[];
}
export const FiltersPanel = observer<IFiltersPanel>(
  ({ alias, defaultFilter, excludedFilters, ...others }) => {
    const lang = langStore.tgt;
    const check = useNodeCheck();
    if (!check.isSVG && !check.isImage) {
      return null;
    }
    const { filters: raw, setFilters } = useFilters();
    const filters = raw ?? [];
    const numFilters = filters.length;
    const filterTitle = `${translate("filters", lang)}${!numFilters ? "" : ` (x${numFilters})`}`;
    const noFiltersDisplay = (
      <Text px="16px" py="4px" color="#939393" fontSize="12px" userSelect="none">
        {translate("no-filter-message", lang)}
      </Text>
    );
    return (
      <SubEditorPanel
        pt="10px"
        panelTitle={
          <SubPanelTitleWithIcon
            h="32px"
            iconProps={{
              mr: "8px",
            }}
            title={filterTitle}
            icon={AddIcon}
            onIconClick={() => {
              const existing = shallowCopy(filters);
              existing.push({ type: defaultFilter ?? "dropShadow", params: {} });
              setFilters({ trace: true })(existing);
            }}
          />
        }
        {...others}
      >
        {filters.length === 0 ? (
          noFiltersDisplay
        ) : (
          <Box px="8px" pt="4px">
            {filters.map(({ type }, i) => (
              <FilterTab
                alias={`${alias}-${type}-${i}`}
                key={`${type}${i}`}
                index={i}
                filters={filters}
                setFilters={setFilters}
                excluded={excludedFilters}
              />
            ))}
          </Box>
        )}
      </SubEditorPanel>
    );
  },
);

const NoliFrameLayerItem = observer<ISubPanel & { title: string; onIconClick: () => void }>(
  ({ title, onIconClick, children, ...others }) => {
    return (
      <SubPanel
        {...getSubEditorTitleProps()}
        py="4px"
        panelTitle={
          <SubPanelTitleWithIcon h="32px" title={title} icon={AddIcon} onIconClick={onIconClick} />
        }
        {...others}
      >
        {children}
      </SubPanel>
    );
  },
);
type NoliFrameLayerCoreProps = ISubPanel & {
  alias: string;
  index: number;
  layerParams: NoliFrameRepeatLayer[];
  setLayerParams: (opt: { trace: boolean }) => (value: NoliFrameRepeatLayer[]) => void;
  lang: Lang;
};
const layerParamsSchema =
  apiSchema.noliTextFrame.options.common.allPacks.repeat.layerParams.bounds.listItem;
function NoliFrameFillPanel({
  alias,
  index,
  layerParams,
  setLayerParams,
  lang,
  ...others
}: NoliFrameLayerCoreProps) {
  const schema = layerParamsSchema.fills.bounds.listItem;
  const { addDefaultNoliFrameFill } = useDefaults();
  const fills = layerParams[index].fills;

  return (
    <NoliFrameLayerItem
      title={translate("text-effect-fill-title", lang)}
      onIconClick={() => {
        const copied = shallowCopy(layerParams);
        addDefaultNoliFrameFill(copied[index]);
        setLayerParams({ trace: true })(copied);
      }}
      {...others}
    >
      {fills?.map((fill, i) => (
        <Flex w="100%" key={i}>
          <SchemaProperty
            identifier={`${alias}-fill-color-${i}`}
            lang={lang}
            noTitle
            w="32px"
            ml="16px"
            apiKey="color"
            apiOption={schema.color}
            value={fill.color!}
            setValue={({ trace }) =>
              (value) => {
                const copied = shallowCopy(layerParams);
                copied[index].fills![i].color = value;
                setLayerParams({ trace })(copied);
              }}
          />
          <SchemaProperty
            identifier={`${alias}-fill-opacity-${i}`}
            lang={lang}
            noTitle
            w="100px"
            apiKey="opacity"
            apiOption={schema.opacity}
            value={fill.opacity!}
            setValue={({ trace }) =>
              (value) => {
                const copied = shallowCopy(layerParams);
                copied[index].fills![i].opacity = value;
                setLayerParams({ trace })(copied);
              }}
          />
          <Spacer />
          <IconButton
            w="32px"
            h="32px"
            mr="8px"
            icon={MinusIcon}
            onClick={() => {
              const copied = shallowCopy(layerParams);
              copied[index].fills!.splice(i, 1);
              setLayerParams({ trace: true })(copied);
            }}
          />
        </Flex>
      )) ?? null}
    </NoliFrameLayerItem>
  );
}
function NoliFrameStrokePanel({
  alias,
  index,
  layerParams,
  setLayerParams,
  lang,
  ...others
}: NoliFrameLayerCoreProps) {
  const schema = layerParamsSchema.strokes.bounds.listItem;
  const { addDefaultNoliFrameStroke } = useDefaults();
  const strokes = layerParams[index].strokes;

  return (
    <NoliFrameLayerItem
      title={translate("text-effect-stroke-title", lang)}
      onIconClick={() => {
        const copied = shallowCopy(layerParams);
        addDefaultNoliFrameStroke(copied[index]);
        setLayerParams({ trace: true })(copied);
      }}
      {...others}
    >
      {strokes?.map((stroke, i) => (
        <Flex w="100%" key={`${i}_${stroke.color}`} wrap="wrap">
          <SchemaProperty
            identifier={`${alias}-stroke-color-${i}`}
            lang={lang}
            noTitle
            w="32px"
            ml="16px"
            apiKey="color"
            apiOption={schema.color}
            value={stroke.color}
            setValue={({ trace }) =>
              (value) => {
                const copied = shallowCopy(layerParams);
                copied[index].strokes![i].color = value;
                setLayerParams({ trace })(copied);
              }}
          />
          <SchemaProperty
            identifier={`${alias}-stroke-opacity-${i}`}
            lang={lang}
            noTitle
            w="64px"
            apiKey="opacity"
            apiOption={schema.opacity}
            value={stroke.opacity}
            setValue={({ trace }) =>
              (value) => {
                const copied = shallowCopy(layerParams);
                copied[index].strokes![i].opacity = value;
                setLayerParams({ trace })(copied);
              }}
          />
          <SchemaProperty
            identifier={`${alias}-stroke-width-${i}`}
            lang={lang}
            w="80px"
            ml="16px"
            captionW="48px"
            inputPadding="48px"
            apiKey="width"
            apiOption={schema.width}
            value={stroke.width}
            setValue={({ trace }) =>
              (value) => {
                const copied = shallowCopy(layerParams);
                copied[index].strokes![i].width = value;
                setLayerParams({ trace })(copied);
              }}
          />
          <SchemaProperty
            identifier={`${alias}-stroke-align-${i}`}
            lang={lang}
            noTitle
            w="64px"
            pl="8px"
            ml="16px"
            selectProps={{
              pr: "8px",
            }}
            apiKey="align"
            apiOption={schema.align}
            value={stroke.align}
            setValue={({ trace }) =>
              (value) => {
                const copied = shallowCopy(layerParams);
                copied[index].strokes![i].align = value;
                setLayerParams({ trace })(copied);
              }}
            dictionary={{
              INSIDE: "INSIDE-stroke",
              CENTER: "CENTER-stroke",
              OUTSIDE: "OUTSIDE-stroke",
            }}
          />
          <SchemaProperty
            identifier={`${alias}-stroke-linejoin-${i}`}
            lang={lang}
            noTitle
            w="64px"
            pl="8px"
            ml="16px"
            selectProps={{
              pr: "8px",
            }}
            apiKey="linejoin"
            apiOption={schema.linejoin}
            value={stroke.linejoin}
            setValue={({ trace }) =>
              (value) => {
                const copied = shallowCopy(layerParams);
                copied[index].strokes![i].linejoin = value;
                setLayerParams({ trace })(copied);
              }}
            dictionary={{
              miter: "miter-stroke",
              bevel: "bevel-stroke",
              round: "round-stroke",
            }}
          />
          <Spacer />
          <IconButton
            w="32px"
            h="32px"
            mr="8px"
            icon={MinusIcon}
            onClick={() => {
              const copied = shallowCopy(layerParams);
              copied[index].strokes!.splice(i, 1);
              setLayerParams({ trace: true })(copied);
            }}
          />
        </Flex>
      )) ?? null}
    </NoliFrameLayerItem>
  );
}
function NoliFrameEffectPanel({
  alias,
  index,
  layerParams,
  setLayerParams,
  lang,
  ...others
}: NoliFrameLayerCoreProps) {
  const schema = layerParamsSchema.effects.bounds.listItem;
  const { addDefaultNoliFrameEffect } = useDefaults();
  const effects = layerParams[index].effects;

  return (
    <NoliFrameLayerItem
      title={translate("text-effect-effect-title", lang)}
      onIconClick={() => {
        const copied = shallowCopy(layerParams);
        addDefaultNoliFrameEffect(copied[index]);
        setLayerParams({ trace: true })(copied);
      }}
      {...others}
    >
      {effects?.map((effect, i) => (
        <Flex w="100%" key={`${i}_${effect.type}`}>
          <SchemaProperty
            identifier={`${alias}-effect-type-${i}`}
            lang={lang}
            noTitle
            w="64px"
            pl="8px"
            ml="16px"
            selectProps={{
              pr: "8px",
            }}
            apiKey="type"
            apiOption={schema.type}
            value={effect.type}
            setValue={({ trace }) =>
              (value) => {
                const copied = shallowCopy(layerParams);
                copied[index].effects![i].type = value;
                setLayerParams({ trace })(copied);
              }}
            dictionary={{
              INSIDE: "INSIDE-stroke",
              CENTER: "CENTER-stroke",
              OUTSIDE: "OUTSIDE-stroke",
            }}
          />
          <SchemaProperty
            identifier={`${alias}-effect-strength-${i}`}
            lang={lang}
            noTitle
            w="64px"
            ml="16px"
            apiKey="strength"
            apiOption={schema.strength}
            value={effect.strength}
            setValue={({ trace }) =>
              (value) => {
                const copied = shallowCopy(layerParams);
                copied[index].effects![i].strength = value;
                setLayerParams({ trace })(copied);
              }}
          />
          <Spacer />
          <IconButton
            w="32px"
            h="32px"
            mr="8px"
            icon={MinusIcon}
            onClick={() => {
              const copied = shallowCopy(layerParams);
              copied[index].effects!.splice(i, 1);
              setLayerParams({ trace: true })(copied);
            }}
          />
        </Flex>
      )) ?? null}
    </NoliFrameLayerItem>
  );
}
const NoliFrameLayerPanel = observer<
  ISubPanel & {
    alias: string;
    activated: boolean[];
    setActivated: (activated: boolean[]) => void;
    index: number;
    layerParams: NoliFrameRepeatLayer[];
    setLayerParams: (opt: { trace: boolean }) => (value: NoliFrameRepeatLayer[]) => void;
  }
>(({ alias, activated, setActivated, index, layerParams, setLayerParams, ...others }) => {
  const lang = langStore.tgt;
  const { boardBg } = themeStore.styles;
  const coreProps = { alias, index, layerParams, setLayerParams, lang };

  return (
    <Center w="100%" h="100%">
      <SubPanel
        w="100%"
        mx="12px"
        pt="4px"
        bg={boardBg}
        panelTitle={
          <SubPanelTitleWithIcon
            h="32px"
            title={`${translate("ith-text-effect-prefix", lang)}${index}${translate(
              "ith-text-effect-postfix",
              lang,
            )}`}
            icon={index > 0 ? MinusIcon : undefined}
            onClick={() => {
              const copied = shallowCopy(activated);
              copied[index] = !copied[index];
              setActivated(copied);
            }}
            onIconClick={() => {
              const copied = shallowCopy(layerParams);
              copied.splice(index, 1);
              setLayerParams({ trace: true })(copied);
            }}
          />
        }
        {...others}
      >
        {activated[index] ? (
          <Flex w="100%" direction="column">
            <NoliFrameFillPanel {...coreProps} />
            <NoliFrameStrokePanel {...coreProps} />
            <NoliFrameEffectPanel {...coreProps} />
          </Flex>
        ) : null}
      </SubPanel>
    </Center>
  );
});
export const NoliFrameLayersPanel = observer<
  ISubPanel & {
    lang: Lang;
    res: ICommonNoliFrameUIInfoResponse | INoliTextFrameUIInfoResponse;
  }
>(({ lang, res, ...others }) => {
  const layerParams = useMemo(() => {
    if (res.type === "noliFrame") {
      return res.uiInfo.params.layerParams;
    }
    return res.uiInfo.common.type === "repeat" ? res.uiInfo.common.params.layerParams ?? [] : [];
  }, [res?.type, res?.uiInfo]);

  const _range = range(0, layerParams.length);

  const [activated, setActivated] = useState(_range.map((i) => i === 0));

  return (
    <SubPanel
      {...getSubEditorTitleProps()}
      w="100%"
      justify="space-between"
      panelTitle={
        <SubPanelTitleWithIcon
          h="32px"
          pb="16px"
          title={translate("text-effects-title", lang)}
          icon={AddIcon}
          onIconClick={() => {
            const copied = shallowCopy(layerParams);
            copied.push({
              fills: [{ color: "#000000", type: "SOLID" }],
              strokes: [],
              effects: [],
              offsetDirection: 0,
              offsetDistance: 0,
            });
            res.setNoliFrameLayerParams({ trace: true })(copied);
          }}
        />
      }
      {...others}
    >
      {_range.map((i) => (
        // check key
        <NoliFrameLayerPanel
          alias={res.displayNode.alias}
          key={i}
          mb="16px"
          activated={activated}
          setActivated={setActivated}
          index={i}
          layerParams={layerParams}
          setLayerParams={res.setNoliFrameLayerParams}
        />
      ))}
    </SubPanel>
  );
});

interface IPathEditorPanel extends FlexProps {
  alias: string;
  lang: Lang;
  options: IPathOptions;
  noTitle?: boolean;
  onColorChange?: (color: string) => void;
  setStroke?: (opt: { trace: boolean }) => (stroke: string) => void;
  setWidth?: (opt: { trace: boolean }) => (width: number) => void;
  setUseFill?: (use: boolean) => void;
  setFill?: (opt: { trace: boolean }) => (fill: string) => void;
}

export const PathEditorPanel = observer<IPathEditorPanel>(
  ({ alias, lang, options, noTitle, setStroke, setWidth, setUseFill, setFill, ...others }) => {
    const { separatorColor } = themeStore.styles;

    if (!options) {
      return null;
    }
    const { fill, stroke, width } = options;
    const useFill = fill !== "none";
    const title = noTitle ?? false ? undefined : translate("brush-options", lang);
    const noTitleProps = noTitle
      ? { mt: "16px", pt: "16px", borderTop: "1px", borderColor: separatorColor }
      : {};
    const pl = "24px";

    return (
      <Flex w="100%" direction="column" {...others}>
        <SubEditorPanel panelTitle={title} {...noTitleProps}>
          {setStroke ? (
            <SchemaProperty
              identifier={`${alias}-brush-stroke`}
              pl={pl}
              pr="32px"
              lang={lang}
              apiKey="brush-stroke"
              apiOption={new APIOption("color", stroke)}
              colorProps={{ opacity: 0.7 }}
              value={stroke}
              setValue={setStroke}
            />
          ) : null}
          {setWidth ? (
            <SchemaProperty
              identifier={`${alias}-brush-width`}
              pl={pl}
              pr="32px"
              lang={lang}
              apiKey="brush-width"
              apiOption={new APIOption("int", width, { lower: 1, upper: 100 })}
              value={width}
              setValue={setWidth}
            />
          ) : null}
          {setUseFill ? (
            <SchemaProperty
              identifier={`${alias}-use-brush-fill`}
              pl={pl}
              pr="32px"
              lang={lang}
              my="8px"
              color="gray"
              apiKey="use-brush-fill"
              apiOption={new APIOption("bool", useFill)}
              value={useFill}
              setValue={() => setUseFill}
            />
          ) : null}
          {useFill && setFill ? (
            <SchemaProperty
              identifier={`${alias}-brush-fill`}
              pl={pl}
              pr="32px"
              lang={lang}
              apiKey="brush-fill"
              apiOption={new APIOption("color", fill)}
              value={fill}
              setValue={setFill}
              colorProps={{ opacity: 0.7 }}
            />
          ) : null}
        </SubEditorPanel>
      </Flex>
    );
  },
);
